import React, { useMemo } from 'react'
import type { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import RangeSlider from '@/components/RangeSlider'
import Select from '@/components/Select'
import { useAppSelector } from '@/hooks/redux'
import { useGetMetadataQuery } from '@/store/apiService'
import { updateshadeGuideFormValue } from '@/store/shadeGuide'
import { REGION, SLIDER_CLASS } from '@/utils/constants'
import { sortAlphabetically } from '@/utils/helper'
import {
  AVAILSPACE_SLIDER_RANGE,
  FRAMEWORK_THICKNESS_OPTIONS,
} from '@/utils/matisseV2/spaceAndThickness'

import {
  MATERIAL_FORM,
  RECIPE_TYPE,
  RESTORATION_METHOD,
  getOptions,
} from '../utils/constants'
import { FORM_FIELDS } from '../utils/formTypes'

interface Metadata {
  [key: string]: any
}

interface MaterialProps {
  control: Control<any>
  recipeTypeValue: string
  restorationMethodValue: string
}

const Material: React.FC<MaterialProps> = ({
  control,
  recipeTypeValue,
  restorationMethodValue,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: Metadata; error: any; isLoading: boolean }

  const options = useMemo(
    () => ({
      ceramicBrands: sortAlphabetically(
        getOptions(metadata, 'available_ceramic_brands') || [],
        'text',
      ),
      frameworkThickness: FRAMEWORK_THICKNESS_OPTIONS,
      frameworkOptions: sortAlphabetically(
        getOptions(metadata, 'available_frameworkoptions') || [],
        'text',
      ),
      stainingBrands: sortAlphabetically(
        getOptions(metadata, 'available_stainingbrands') || [],
        'text',
      ),
    }),
    [metadata],
  )

  const availSpace = useAppSelector(
    (state) => state.shadeGuide.form as Partial<Record<FORM_FIELDS, any>>,
  )

  return (
    <>
      <Card>
        <Select
          options={options.frameworkOptions}
          title={t(MATERIAL_FORM.frameworkColor.label)}
          tooltipText={t(MATERIAL_FORM.frameworkColor.tooltip)}
          control={control}
          name={FORM_FIELDS.FRAMEWORK_COLOR}
          onFormValueChange={(name, value) =>
            dispatch(
              updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
            )
          }
          renderValue={(selected: any) =>
            selected ? (
              selected?.join(' - ')
            ) : (
              <span style={{ color: '#999999' }}>Select</span>
            )
          }
        />
      </Card>
      <Card>
        <Select
          options={options.frameworkThickness}
          title={t(MATERIAL_FORM.frameworkThickness.label)}
          tooltipText={t(MATERIAL_FORM.frameworkThickness.tooltip)}
          control={control}
          name={FORM_FIELDS.FRAMEWORK_OPTIONS}
          onFormValueChange={(name, value) =>
            dispatch(
              updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
            )
          }
          direction="row"
        />
      </Card>
      <Card>
        <Select
          options={options.stainingBrands}
          title={t(MATERIAL_FORM.stainingBrand.label)}
          tooltipText={t(MATERIAL_FORM.stainingBrand.tooltip)}
          control={control}
          name={FORM_FIELDS.STAINING_BRAND}
          onFormValueChange={(name, value) =>
            dispatch(
              updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
            )
          }
          direction="row"
        />
      </Card>
      {restorationMethodValue === RESTORATION_METHOD.LAYERING && (
        <Card>
          <Select
            options={options.ceramicBrands}
            title={t(MATERIAL_FORM.ceramicBrand.label)}
            tooltipText={t(MATERIAL_FORM.ceramicBrand.tooltip)}
            control={control}
            name={FORM_FIELDS.BRAND}
            onFormValueChange={(name, value) =>
              dispatch(
                updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
              )
            }
            direction="row"
          />
        </Card>
      )}
      {restorationMethodValue === RESTORATION_METHOD.LAYERING && (
        <Card>
          <LabelWithTooltip
            label={t(MATERIAL_FORM.availableSpace.label)}
            tooltipText={t(MATERIAL_FORM.availableSpace.tooltip)}
          />
          {recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE && (
            <RangeSlider
              name={FORM_FIELDS.AVAILABLE_SPACE_CERVICAL}
              label={t(REGION.CERVICAL)}
              control={control}
              sliderRange={AVAILSPACE_SLIDER_RANGE}
              step={0.25}
              className={SLIDER_CLASS.CERVICAL}
              onFormValueChange={(name, value) =>
                dispatch(
                  updateshadeGuideFormValue({
                    name: name as FORM_FIELDS,
                    value,
                  }),
                )
              }
              selectedValue={
                availSpace?.[FORM_FIELDS.AVAILABLE_SPACE_CERVICAL] || 0.5
              }
            />
          )}
          <RangeSlider
            name={FORM_FIELDS.AVAILABLE_SPACE_MIDDLE}
            label={t(REGION.MIDDLE)}
            control={control}
            sliderRange={AVAILSPACE_SLIDER_RANGE}
            step={0.25}
            className={SLIDER_CLASS.MIDDLE}
            onFormValueChange={(name, value) =>
              dispatch(
                updateshadeGuideFormValue({ name: name as FORM_FIELDS, value }),
              )
            }
            selectedValue={
              availSpace?.[FORM_FIELDS.AVAILABLE_SPACE_MIDDLE] || 0.5
            }
          />
          {recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE && (
            <RangeSlider
              name={FORM_FIELDS.AVAILABLE_SPACE_INCISAL}
              label={t(REGION.INCISAL)}
              control={control}
              sliderRange={AVAILSPACE_SLIDER_RANGE}
              step={0.25}
              className={SLIDER_CLASS.INCISAL}
              onFormValueChange={(name, value) =>
                dispatch(
                  updateshadeGuideFormValue({
                    name: name as FORM_FIELDS,
                    value,
                  }),
                )
              }
              selectedValue={
                availSpace?.[FORM_FIELDS.AVAILABLE_SPACE_INCISAL] || 0.5
              }
            />
          )}
        </Card>
      )}
    </>
  )
}

export default Material
